/* ==========================================================================
   Ozeret -- application state

   A small external store rather than Context + useReducer: the action surface
   is wide and flat, and the screens read the record arrays directly.

   Every action that changes anything goes to the server and swaps in the world
   that comes back. Nothing is written locally and hoped for.
   ========================================================================== */

import { useSyncExternalStore } from 'react';
import type { Clock, IsoDate, Recurrence, Review, Role, Weekday } from './types';
import { ApiError, api, type Bootstrap, type HiddenPair, type Me } from './api';
import { askAgain } from './lib/push';
import { NOTIF_SEEN, hydrate } from './data/live';
import { AVAILABILITY, BOOKINGS, CLEANERS, NOTIFICATIONS, NOTIFY_PREFS, REQUESTS, THREADS } from './data/seed';
import { DOW } from './data/catalog';
import { byId, cleaner, house, job } from './lib/domain';
import { TODAY_ISO, addDays, fmtDate, parseDate, setClock } from './lib/time';
import { isRtl, setLang as setLangRaw, t, tf } from './lib/i18n';
import { PARENT, pathFor, resolvePath, type Arrival } from './lib/routes';
import { loadTranslationStatus } from './components/Translatable';

/* ------------------------------------------------------------------ shape */

export type ScreenName =
  | 'c_jobs' | 'c_job' | 'c_calendar' | 'c_editweek' | 'c_bookings' | 'c_booking'
  | 'c_messages' | 'c_chat' | 'c_profile' | 'c_editprofile' | 'c_inprogress'
  | 'h_find' | 'h_cleaner' | 'h_post' | 'h_editrequest' | 'h_requests' | 'h_request'
  | 'h_joblist' | 'h_booking' | 'h_messages' | 'h_chat' | 'h_profile' | 'h_edithome'
  | 'h_pastjob'
  | 'notifications' | 'review' | 'feedback' | 'settings' | 'admin' | 'sql';

export type SheetName =
  | 'jobfilter' | 'peoplefilter' | 'jobsort' | 'peoplesort' | 'pickdate'
  | 'counter' | 'negotiate' | 'report' | 'note' | 'dayedit' | 'timeoff'
  | 'lang' | 'property' | 'customjob' | 'hide' | 'once' | 'week';

export interface Sheet {
  name: SheetName;
  id?: string;
  /** For the hide sheet: who, and which way round. */
  who?: 'house' | 'cleaner';
  name2?: string;
  /** For the week sheet: which date of the series. */
  date?: string;
  i?: number;
  mode?: 'available' | 'block' | 'off';
  from?: Clock; to?: Clock; wFrom?: Clock; wTo?: Clock;
  reason?: string;
  rate?: number | null;
  hours?: number;
}

export interface JobFilter {
  areas: string[]; tags: string[]; langs: string[];
  minRate: number; minHours: number; date: IsoDate | '';
  sort: 'match' | 'soonest' | 'pay' | 'hours' | 'near';
  mode: 'list' | 'map';
  /** Free text: a home's name, or a word from what they wrote. */
  q: string;
}

export interface PeopleFilter {
  areas: string[]; tags: string[]; langs: string[];
  rateMin: number; rateMax: number;
  gender: 'any' | 'female' | 'male';
  minRating: number; date: IsoDate | '';
  sort: 'match' | 'cheap' | 'dear' | 'rated';
  /** Who is free in the next couple of hours, not just some time today. */
  now: boolean;
  /** Free text: a cleaner's name. */
  q: string;
}

export interface Draft {
  id?: string; houseId: string; jobIds: string[];
  date: IsoDate; start: Clock; hours: number; rate: number;
  payMethods: string[]; notes: string; recurring: Recurrence | null;
}

export interface PastJobDraft {
  cleanerId: string; date: IsoDate; start: Clock;
  hours: number; rate: number; jobIds: string[];
}

type EditDraft = Record<string, any>;

interface Nav { view: ScreenName; params: { id?: string } }

export interface AppState extends Nav {
  booted: boolean;
  busy: boolean;
  me: Me | null;
  /** The free month, a subscription, or browsing. Null until booted. */
  billing: Bootstrap['billing'] | null;
  /** Which control is mid-flight, so a spinner lands on the thing pressed
   *  rather than on whichever button happens to read `busy`. */
  busyWith: string | null;
  /** What the profile is still missing, or null while it is fine. */
  profile: Bootstrap['profile'] | null;
  role: Role;
  stack: Nav[];
  houseId: string;
  cleanerId: string;
  sheet: Sheet | null;
  toast: string | null;
  filters: { jobs: JobFilter; people: PeopleFilter };
  draft: Draft | null;
  pastJob: PastJobDraft | null;
  edit: EditDraft | null;
  reviewDraft: Record<string, any>;
  selDays: IsoDate[];
  calMode: 'today' | 'week' | 'month';
  reqMode: 'week' | 'open' | 'history';
  /** People you have stepped away from. Loaded on demand, in Settings. */
  hidden: HiddenPair[];
  /** 0 is this week; negative is back, positive is forward. */
  weekOffset: number;
  /** Pairs the cleaner has waved away, for this session. */
  hiddenPairs: string[];
  /** Admin only: force the layout, to check the other one without another
   *  device. 'auto' follows the window, as it does for everybody else. */
  viewport: 'auto' | 'mobile' | 'desktop';
  jobsMode: 'upcoming' | 'applied' | 'finished';
  ctx: string | null;
  fb: { topic: string; text: string };
  lang: string;
  signInError: string | null;
  /** Code lifted from an /i/CODE invite link, so testers only type a name. */
  inviteFromLink: string;
  /** Set when the page was opened at /s/<token>. */
  shareToken: string;
  share: ShareView | null;
  /** Which side's guided first run to show, if any. */
  tour: Role | null;
}

export const blankJobFilter = (): JobFilter => ({
  areas: [], tags: [], langs: [], minRate: 0, minHours: 0, date: '', sort: 'match',
  mode: 'list', q: '',
});
export const blankPeopleFilter = (): PeopleFilter => ({
  areas: [], tags: [], langs: [], rateMin: 15, rateMax: 200, gender: 'any',
  minRating: 0, date: '', now: false, sort: 'match', q: '',
});

export const state: AppState = {
  booted: false, busy: false, busyWith: null, me: null,
  role: 'house', view: 'h_find', params: {}, stack: [],
  houseId: '', cleanerId: '',
  sheet: null, toast: null,
  filters: { jobs: blankJobFilter(), people: blankPeopleFilter() },
  draft: null, pastJob: null, edit: null, reviewDraft: {},
  selDays: [], calMode: 'today', reqMode: 'week', jobsMode: 'upcoming', weekOffset: 0, hiddenPairs: [],
  viewport: 'auto',
  hidden: [],
  ctx: null, fb: { topic: '', text: '' }, lang: 'en', signInError: null,
  inviteFromLink: readInviteFromUrl(),
  shareToken: readShareFromUrl(),
  share: null,
  tour: null,
  billing: null,
  profile: null,
};

/** An invite link is /i/CODE. Read it once, then tidy the address bar so the
 *  code is not left sitting in history or shoulder-surfable. */
/* A shared link. Read once, before anything else decides what to draw: it
   has to survive being opened by somebody with no account at all. */
export function readShareFromUrl(): string {
  const m = /^\/s\/([A-Za-z0-9]{6,24})\/?$/.exec(window.location.pathname);
  return m ? m[1] : '';
}

function readInviteFromUrl(): string {
  const m = /^\/i\/([A-Za-z0-9]{4,12})\/?$/.exec(window.location.pathname);
  if (!m) return '';
  window.history.replaceState({}, '', '/');
  return m[1].toUpperCase();
}

/** Which properties belong to whoever is signed in. Refilled on every load,
 *  so the screens can keep importing it as a plain array. */
export const HOUSE_IDS: string[] = [];

export const activeHouse = () => house(state.houseId);
/** Undefined for a home account. Screens that are genuinely cleaner-only can
 *  use it directly; anything shared must check. */
export const activeCleaner = () => cleaner(state.cleanerId) as ReturnType<typeof cleaner> | undefined;

/* --------------------------------------------------------- subscriptions */

let version = 0;
const listeners = new Set<() => void>();

export interface ShareView {
  kind: 'request' | 'cleaner' | 'house';
  gone: boolean;
  request?: Record<string, any>;
  home?: Record<string, any>;
  cleaner?: Record<string, any>;
  openJobs?: Record<string, any>[];
}

export function commit(): void {
  version++;
  syncUrl();
  listeners.forEach((l) => l());
}

/* ------------------------------------------------------------ addresses */

/** Keep the address bar on the screen that is showing.

    Every change of screen ends in commit(), however it happened -- a tap, a
    tab, the back gesture, a booking that jumps to its confirmation -- so this
    one place is enough. It only ever REPLACES the current entry's address:
    nav() still decides when a new history entry is made, exactly as before,
    and each entry simply carries the right address now. */
function syncUrl(): void {
  if (!state.booted || state.shareToken) return;
  const here = window.location.pathname;
  let want: string;
  if (state.me) {
    want = pathFor(state.view, state.params);
  } else {
    /* Signed out: the front door. An address they arrived at is remembered
       in pendingPath and opened after they sign in. */
    if (here === '/' || /^\/(s|i)\//.test(here)) return;
    want = '/';
  }
  if (here === want) return;
  try {
    window.history.replaceState(window.history.state, '', want + (state.me ? '' : window.location.search));
  } catch { /* no history in some embedded views */ }
}

/** Does this id still point at something this person can see? A job that has
    been filled, a chat that is gone, or somebody else's request is not. */
function stillThere(view: ScreenName, id: string): boolean {
  switch (view) {
    case 'h_cleaner': return !!byId(CLEANERS, id);
    case 'h_request': case 'h_editrequest': {
      const r = byId(REQUESTS, id);
      return !!r && HOUSE_IDS.includes(r.houseId);
    }
    case 'c_job': return !!byId(REQUESTS, id);
    case 'h_booking': case 'c_booking': case 'c_inprogress': case 'review':
      return !!byId(BOOKINGS, id);
    case 'h_chat': case 'c_chat': return !!byId(THREADS, id);
    default: return true;
  }
}

function arrivalFor(path: string): Arrival | null {
  if (!state.me) return null;
  /* A job that has since become this cleaner's booking opens the booking --
     a refresh on /work/<id> after the home chose her, or an old link to it. */
  const job = /^\/work\/([^/]+)\/?$/.exec(path);
  if (job && state.role === 'cleaner') {
    const id = decodeURIComponent(job[1]);
    const r = byId(REQUESTS, id);
    if (!r || r.status !== 'open') {
      const mine = BOOKINGS.find((b) => b.requestId === id && b.cleanerId === state.cleanerId && b.status !== 'cancelled');
      if (mine) return { view: 'c_booking', params: { id: mine.id } };
    }
  }
  return resolvePath(path, state.role, stillThere, !!state.me.isAdmin);
}

/** Open a screen reached by address. A screen that lives inside a list gets
    the list put behind it -- in our stack and in the browser's history -- so
    Back goes to the list rather than out of the app. */
function openArrival(a: Arrival): void {
  const parent = PARENT[a.view];
  state.sheet = null;
  state.stack = [];
  if (parent) {
    state.stack = [{ view: parent, params: {} }];
    try {
      window.history.replaceState({ ozeret: 0 }, '', pathFor(parent));
      window.history.pushState({ ozeret: 1 }, '', pathFor(a.view, a.params));
    } catch { /* no history */ }
  }
  state.view = a.view;
  state.params = a.params;
}

/** An app address somebody opened while signed out: a bookmark, a link from
    a message, a refresh after the session ended. Opened once they are in. */
let pendingPath: string | null = null;

function openPendingPath(): void {
  const p = pendingPath;
  pendingPath = null;
  if (!p || !state.me) return;
  const a = arrivalFor(p);
  if (a) openArrival(a);
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function useStore(): number {
  return useSyncExternalStore(subscribe, () => version, () => version);
}

/* --------------------------------------------------------------- loading */

function absorb(b: Bootstrap): void {
  setClock(b.serverTime);
  hydrate(b);
  state.me = b.me;
  state.billing = b.billing ?? null;
  /* Paying now, so stop offering to take them to Stripe. */
  if (b.billing?.standing === 'paid') setWantsSubscribe(false);
  state.profile = b.profile ?? null;
  HOUSE_IDS.length = 0;
  if (b.me) {
    HOUSE_IDS.push(...b.me.houseIds);
    state.role = b.me.role;
    state.cleanerId = b.me.cleanerId ?? '';
    if (!state.houseId || !HOUSE_IDS.includes(state.houseId)) {
      state.houseId = b.me.houseIds[0] ?? '';
    }
  }
}

/** Runs once at start-up: are we already signed in? */
/** Turn something you can see into a link you can send. */
export async function shareLink(
  kind: 'request' | 'cleaner' | 'house', id?: string,
): Promise<void> {
  try {
    const r = await api.share(kind, id);
    const url = window.location.origin + '/s/' + r.token;
    const title = kind === 'request'
      ? 'A cleaning job on Ozeret'
      : kind === 'house'
        ? 'A home looking for a cleaner on Ozeret'
        : 'A cleaner on Ozeret';

    /* The phone's own share sheet where there is one -- that is how people
       actually send things to each other here, into WhatsApp. */
    const nav = window.navigator as Navigator & { share?: (d: unknown) => Promise<void> };
    if (typeof nav.share === 'function') {
      await nav.share({ title, url });
      return;
    }
    await window.navigator.clipboard.writeText(url);
    toast(t('tx_s_link'));
  } catch (e) {
    toast(e instanceof ApiError ? e.message : t('tx_s_nolink'));
  }
}

export async function loadShare(): Promise<void> {
  if (!state.shareToken) return;
  try {
    state.share = await api.readShare(state.shareToken);
  } catch {
    state.share = { kind: 'request', gone: true };
  }
  commit();
}

/** What a shared link was about, held until there is somebody signed in to
    show it to. "Open Ozeret" used to drop everybody on their home screen, so
    a cleaner sent a home's link landed on Find work and had to go looking
    for the very thing she had just been shown. */
let shareTarget: { kind: 'request' | 'cleaner'; id: string } | null = null;

/** Open what the link was about, if this person can see it. Called after
    every way of arriving signed in. */
function openShareTarget(): void {
  const target = shareTarget;
  if (!target || !state.me) return;
  shareTarget = null;
  if (target.kind === 'cleaner') {
    if (state.role === 'house' && byId(CLEANERS, target.id)) nav('h_cleaner', { id: target.id });
    return;
  }
  const r = byId(REQUESTS, target.id);
  if (state.role === 'cleaner') {
    if (r) nav('c_job', { id: r.id });
    else toast(t('tx_s_notshowing'));
  } else if (r && state.me.houseIds.includes(r.houseId)) {
    nav('h_request', { id: r.id });
  }
}

/** Leave the shared page and carry on into the app proper. */
export function dismissShare(): void {
  const s = state.share;
  if (s && !s.gone) {
    /* A home's link opens its soonest open job: that is the thing a cleaner
       can act on, and the job screen shows the home with it. */
    const id = s.kind === 'request' ? s.request?.id
      : s.kind === 'house' ? s.openJobs?.[0]?.id
        : s.cleaner?.id;
    if (id) shareTarget = { kind: s.kind === 'cleaner' ? 'cleaner' : 'request', id };
  }
  state.shareToken = '';
  state.share = null;
  window.history.replaceState({}, '', '/');
  commit();
  openShareTarget();
}

export async function boot(): Promise<void> {
  try {
    absorb(await api.bootstrap());
    if (state.me) void loadTranslationStatus();
  } catch {
    state.me = null;
  }
  state.booted = true;
  if (state.me) {
    state.view = state.me.role === 'house' ? 'h_find' : 'c_jobs';
    /* Arrived at an address -- a refresh, a bookmark, a new tab: open it. */
    const a = arrivalFor(window.location.pathname);
    if (a) openArrival(a);
  } else if (window.location.pathname !== '/' && !/^\/(s|i)\//.test(window.location.pathname)) {
    pendingPath = window.location.pathname;
  }
  commit();
}

/** Fetch everything again. Used by pull-to-refresh; deliberately quiet, since
 *  the gesture is its own feedback. */
export async function refresh(): Promise<void> {
  try {
    absorb(await api.bootstrap());
  } catch {
    /* Offline or asleep. Leave what is on screen rather than blanking it. */
  }
  commit();
}

/** Keep the screen current without anybody pulling to refresh.

    Oster, testing with a home on one device and a cleaner on another: "unless
    I refresh the page I do not see the update." Nothing ever looked for
    changes on its own -- the world was fetched once at start and then only
    when this device itself changed something, so an invitation, a message or
    an application from the OTHER side stayed invisible until a reload. For a
    marketplace whose whole point is two people answering each other, that is
    the app looking dead.

    So it asks again: every few seconds while a conversation is open, less
    often elsewhere, at once when the tab comes back into view, and never
    while the page is hidden. A full fetch is about 33 KB and 0.2s, which is
    cheap at this size. It is polling rather than a live connection, which is
    the right trade for now and the thing to revisit if the user count grows
    by a lot.

    It never runs mid-change: if this device is saving something, the poll
    waits rather than racing it. Forms being filled in live outside the
    fetched data, so a refresh cannot wipe what somebody is typing. */
let polling: ReturnType<typeof setTimeout> | null = null;

export function listenForUpdates(): void {
  const inChat = () => state.view === 'h_chat' || state.view === 'c_chat';
  const tick = async () => {
    polling = null;
    if (document.visibilityState === 'visible' && state.me && !state.busy) {
      await refresh();
    }
    polling = setTimeout(tick, inChat() ? 6000 : 20000);
  };
  const now = () => {
    if (document.visibilityState !== 'visible') return;
    if (polling) clearTimeout(polling);
    void tick();
  };
  document.addEventListener('visibilitychange', now);
  window.addEventListener('focus', now);
  polling = setTimeout(tick, 20000);
}

/** Wraps a mutation: swap in the returned world, surface failures as a toast. */
async function mutate<T extends { state: Bootstrap }>(
  fn: () => Promise<T>,
  after?: (r: T) => void,
): Promise<T | null> {
  state.busy = true;
  commit();
  try {
    const r = await fn();
    absorb(r.state);
    after?.(r);
    return r;
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Something went wrong';
    state.toast = msg;
    scheduleToastClear();
    if (err instanceof ApiError && err.status === 401) {
      state.me = null;
      state.stack = [];
    }
    return null;
  } finally {
    state.busy = false;
    commit();
  }
}

/* ------------------------------------------------------------ navigation */

/* Every screen we push is also pushed onto the browser's own history.

   Without this the phone has no idea anything happened: Ozeret kept its own
   private stack, so an iPhone edge-swipe and an Android back button either
   did nothing or threw you out of the app entirely. */
export function nav(view: ScreenName, params: { id?: string } = {}): void {
  state.stack.push({ view: state.view, params: state.params });
  state.view = view;
  state.params = params;
  state.sheet = null;
  try {
    /* The entry remembers how deep it is, so Back knows whether there is
       anything of ours behind it. Its address is filled in by syncUrl. */
    window.history.pushState({ ozeret: depth() + 1 }, '');
  } catch { /* no history in some embedded views; the app still works */ }
  commit();
}

/** How many of our screens are behind this one in the browser's history. */
function depth(): number {
  const s = window.history.state as { ozeret?: number } | null;
  return typeof s?.ozeret === 'number' ? s.ozeret : 0;
}

/** The in-app back button. Goes through the browser so that its history and
    ours can never drift apart -- popState below does the actual work. */
export function back(): void {
  if (state.sheet) { state.sheet = null; commit(); return; }

  /* Several things land you somewhere with nothing behind them -- finishing a
     job, logging a past one, choosing an applicant all clear the stack and
     then show a screen. The arrow was still drawn, and did nothing.

     A back control that does nothing is worse than no back control, so an
     empty stack goes home rather than sitting there. */
  if (depth() === 0) {
    const parent = PARENT[state.view];
    if (parent) { tab(parent); return; }
    tab(state.role === 'house' ? 'h_requests' : 'c_bookings');
    return;
  }
  try {
    window.history.back();
  } catch {
    showWhatTheAddressSays();
  }
}

/** The browser moved -- Back or Forward, our arrow, the phone's gesture, the
    hardware key -- and its address says where we are now.

    This used to pop our own stack on every move, as if every move were Back.
    Forward is a move too: after Back then Forward the stack was empty, so
    the next Back (or the X on a desktop pane) dropped people on a different
    tab entirely. The address is the truth; show what it says. */
function showWhatTheAddressSays(): void {
  const a = arrivalFor(window.location.pathname);
  if (a) {
    state.view = a.view;
    state.params = a.params;
    const parent = PARENT[a.view];
    state.stack = parent ? [{ view: parent, params: {} }] : [];
  }
  commit();
}

/** Texts about their own jobs, on or off. On is always the person's own
    tap: the question after signing in, or the switch in Settings. */
export async function setTexts(on: boolean, via: 'ask' | 'settings'): Promise<void> {
  try {
    absorb((await api.setTexts(on, via, state.lang)).state);
    toast(on
      ? (state.lang === 'es' ? 'Listo. Te enviaremos mensajes sobre tus trabajos.' : 'Done. We will text you about your jobs.')
      : (state.lang === 'es' ? 'No te enviaremos más mensajes.' : 'Texts are off.'));
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t('tx_s_nochange'));
  }
  commit();
}

/** A notification was tapped while the app was already open. Fetch what is
    new first -- the notification is about something that just happened --
    then go to the screen it names, with its list behind it. */
export async function openFromNotification(path: string): Promise<void> {
  await refresh();
  if (!state.me) return;
  const a = arrivalFor(path);
  if (a) {
    openArrival(a);
    commit();
  }
}

/** Wire the phone's own back gesture to the app. Called once, at start-up. */
export function listenForBack(): void {
  window.addEventListener('popstate', () => {
    /* A sheet is the top thing on screen, so it goes first. We never pushed
       an entry for it, so put one back to keep the depths in step. */
    if (state.sheet) {
      state.sheet = null;
      commit();
      try {
        window.history.pushState({ ozeret: depth() + 1 }, '');
      } catch { /* nothing to restore */ }
      commit();
      return;
    }
    showWhatTheAddressSays();
  });
}

/** Swap what this history entry shows, without adding one: the thing on
    screen turned into something else (a job that became her booking). */
export function becomes(view: ScreenName, params: { id?: string } = {}): void {
  const parent = PARENT[view];
  state.stack = parent ? [{ view: parent, params: {} }] : [];
  state.view = view;
  state.params = params;
  state.sheet = null;
  commit();
}

export function tab(view: ScreenName): void {
  state.stack = [];
  state.view = view;
  state.params = {};
  state.sheet = null;
  commit();
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleToastClear() {
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { state.toast = null; commit(); }, 2800);
}
export function toast(msg: string): void {
  state.toast = msg;
  commit();
  scheduleToastClear();
}

export function openSheet(sheet: Sheet): void { state.sheet = sheet; commit(); }
export function closeSheet(): void { state.sheet = null; commit(); }

export function setLang(id: string): void {
  setLangRaw(id);
  state.lang = id;
  applyDirection(id);
  commit();
}

/** Hebrew and Yiddish read right to left. Setting `dir` on the document lets
 *  the browser mirror the whole layout -- flex order, text alignment, even
 *  which side a scrollbar sits on -- without a second stylesheet. */
export function applyDirection(id: string): void {
  const rtl = isRtl(id);
  document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', id);
}

/** A second home. Switches to it once it exists, because somebody who has
 *  just added a property wants to set it up, not go looking for it. */
export function addProperty(displayName: string, zip: string): Promise<unknown> {
  return mutate(
    () => api.addHouse({ displayName, zip }),
    (r) => {
      const made = (r as { id?: string }).id;
      if (made) state.houseId = made;
      state.sheet = null;
      toast('Added. This is now the property you are looking at.');
    },
  );
}

/** Give a request that nobody answered another day, rather than typing the
 *  whole thing again. The old one closes; this is a new one, so its history
 *  stays legible. */
export function renewRequest(id: string, date?: string): Promise<unknown> {
  return mutate(
    () => api.renewRequest(id, date),
    (r) => {
      const made = (r as { id?: string }).id;
      if (made) nav('h_request', { id: made });
      toast('Posted again for ' + fmtDate((r as { date: string }).date, { long: true }) + '.');
    },
  );
}

export function setProperty(id: string): void {
  state.houseId = id;
  state.sheet = null;
  state.draft = null;
  toast('Switched to ' + house(id).displayName);
}

export function toggleIn<T>(arr: T[], v: T): void {
  const i = arr.indexOf(v);
  if (i >= 0) arr.splice(i, 1);
  else arr.push(v);
}

/* --------------------------------------------------------------- sign in */

/* Signing up and signing in by phone. Two steps: ask for a code, then hand
   it back. Whether an account exists is the server's business; this only
   carries the answers. */

export async function startPhone(
  phone: string,
): Promise<
  { delivery: 'sms' | 'admin'; known: boolean; role: string | null; roles: Role[] } | null
> {
  state.busy = true;
  state.signInError = null;
  commit();
  try {
    const r = await api.phoneStart(phone);
    /* An older server names one side and no list; the one side is the list. */
    const roles = (r.roles ?? (r.role ? [r.role] : []))
      .filter((x): x is Role => x === 'house' || x === 'cleaner');
    return { delivery: r.delivery, known: r.known, role: r.role, roles };
  } catch (err) {
    state.signInError = err instanceof ApiError ? err.message : 'Something went wrong';
    return null;
  } finally {
    state.busy = false;
    commit();
  }
}

/** True when signed in. 'need-details' when the server wants a side and a
 *  name because this number is new; 'need-side' when it wants only a side,
 *  because the number holds an account on each and it will not guess between
 *  them. False on any other refusal. */
export interface SignupPlace {
  zip?: string;
  address?: string;
  areas?: string[];
  /** Pressed "use it anyway" on an address the geocoder did not recognise. */
  addressConfirmed?: boolean;
  /** A promo from Oster, or a friend's share code. One field either way. */
  promo?: string;
  /** Ticked "Text me about my jobs". Only ever true from that tick. */
  smsOk?: boolean;
  lang?: string;
}

/* Somebody who chose to pay on the way in.

   Kept in localStorage rather than in state because it has to survive the
   round trip to Stripe and back, and because the whole point of it is to
   outlive a redirect that did not happen. Oster, testing on his phone: "when
   I clicked subscribe and start using ozeret I was never prompted an actual
   subscribe page." Whatever swallowed that redirect, the answer is that
   choosing to pay must leave a mark the app can act on afterwards, instead
   of being a one-shot that silently misses. Cleared the moment they are
   actually paying. */
const WANTS_KEY = 'ozeret.wantsSubscribe';

export function wantsSubscribe(): boolean {
  try { return localStorage.getItem(WANTS_KEY) === '1'; } catch { return false; }
}

export function setWantsSubscribe(on: boolean): void {
  try {
    if (on) localStorage.setItem(WANTS_KEY, '1');
    else localStorage.removeItem(WANTS_KEY);
  } catch { /* private window, or storage turned off; the bar just will not show */ }
}

export async function checkPhone(
  phone: string, code: string, role: Role | null, name: string,
  place: SignupPlace = {},
): Promise<true | false | 'need-details' | 'need-side' | 'need-place' | 'address-doubt'> {
  state.busy = true;
  state.signInError = null;
  commit();
  try {
    const r = await api.phoneCheck({ phone, code, role, name, ...place });
    /* What the code did, said out loud. A mistyped promo does not stop the
       account being made, so this is the only place anybody finds out. */
    if (r.promo) toast(r.promo.message);
    absorb(r.state);
    state.view = state.role === 'house' ? 'h_find' : 'c_jobs';
    state.stack = [];
    if (r.created) state.tour = state.role;
    openShareTarget();
    openPendingPath();
    askAgain();
    return true;
  } catch (err) {
    state.signInError = err instanceof ApiError ? err.message : 'Something went wrong';
    if (err instanceof ApiError && err.status === 400) {
      /* Not a wrong code: the server is missing something it needs. Either
         the number is new, and it wants a side and a name, or it already
         holds an account on each side and wants to be told which. `choose`
         is what separates the two -- it lists the accounts already there. */
      const asked = err.body as {
        needRole?: boolean; needName?: boolean; needPlace?: boolean;
        addressUnverified?: boolean; choose?: string[];
      };
      if (asked.choose?.length) {
        state.signInError = null;
        return 'need-side';
      }
      if (asked.needRole || asked.needName) {
        state.signInError = null;
        return 'need-details';
      }
      /* An address nothing could find. Not a refusal: the Census does not
         know every private lane, so the app offers "use it anyway" rather
         than arguing with somebody about where they live. */
      if (asked.addressUnverified) return 'address-doubt';
      /* The place fields are already on screen by the time this can happen,
         so the message stays -- it says which one is wrong. */
      if (asked.needPlace) return 'need-place';
    }
    return false;
  } finally {
    state.busy = false;
    state.busyWith = null;
    commit();
  }
}

/** Reaching an existing account with its permanent code.

    This went missing when sign-in became phone-only, and with it the only way
    back in that does not need somebody to read a code out of a database. It
    is the second-device path and the locked-out path both. */
export async function signInExisting(code: string): Promise<boolean> {
  state.busy = true;
  state.signInError = null;
  commit();
  try {
    const r = await api.signIn(code);
    absorb(r.state);
    state.view = state.role === 'house' ? 'h_find' : 'c_jobs';
    state.stack = [];
    openShareTarget();
    openPendingPath();
    askAgain();
    return true;
  } catch (err) {
    state.signInError = err instanceof ApiError ? err.message : 'Something went wrong';
    return false;
  } finally {
    state.busy = false;
    commit();
  }
}

/** The account goes. The history stays. */
export async function deleteAccount(reason: string | null = null, note = ''): Promise<boolean> {
  state.busy = true;
  commit();
  try {
    await api.deleteAccount(reason, note);
    state.me = null;
    state.stack = [];
    state.sheet = null;
    toast(t('tx_s_deleted'));
    return true;
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong');
    return false;
  } finally {
    state.busy = false;
    commit();
  }
}

export async function signInDemo(role: Role): Promise<void> {
  /* Which control is working, not merely that something is.
     `busy` is global, and the sign-in screen's submit button renders
     `busy ? 'One moment…' : 'Send me a code'` -- so pressing "Look around as
     a home" put the spinner on a different button, in a different card, while
     the button actually pressed just went grey. */
  state.busyWith = 'demo-' + role;
  state.busy = true;
  state.signInError = null;
  commit();
  try {
    const r = await api.demo(role);
    absorb(r.state);
    state.view = role === 'house' ? 'h_find' : 'c_jobs';
    state.stack = [];
    openPendingPath();
  } catch (err) {
    state.signInError = err instanceof ApiError ? err.message : 'Something went wrong';
  } finally {
    state.busy = false;
    commit();
  }
}

export async function signOut(): Promise<void> {
  await api.signOut().catch(() => {});
  state.me = null;
  state.stack = [];
  state.sheet = null;
  state.ctx = null;
  HOUSE_IDS.length = 0;
  commit();
}

/* --------------------------------------------------------- cleaner side */

export const applyToRequest = (requestId: string) =>
  mutate(() => api.apply(requestId, {}), () =>
    toast(t('tx_s_sent')));

export const counterOffer = (requestId: string, rate: number, message: string) =>
  mutate(() => api.apply(requestId, { counterRate: rate, message }), () => {
    state.sheet = null;
    toast(tf('tx_s_offer', { rate: '$' + rate }));
  });

export const acceptInvite = (requestId: string) =>
  mutate(() => api.apply(requestId, {}), () =>
    toast(t('tx_s_accepted')));

export const declineInvite = (requestId: string) =>
  mutate(() => api.withdraw(requestId), () => toast(t('tx_s_declined')));

export const withdraw = (requestId: string) =>
  mutate(() => api.withdraw(requestId), () => toast(t('tx_s_withdrawn')));

/* ------------------------------------------------------------ house side */

export const chooseApplicant = (requestId: string, cleanerId: string) =>
  mutate(() => api.choose(requestId, cleanerId), (r) => {
    state.ctx = null;
    state.stack = [];
    state.view = 'h_booking';
    state.params = { id: r.id! };
    toast(t('tx_s_booked'));
  });

/* The toast used to say "Invited" whatever came back, including when the
   server had done nothing because the cleaner was already on this request.
   That is how an invitation can be sent and never arrive. */
export const inviteToRequest = (requestId: string, cleanerId: string) =>
  mutate(() => api.invite(requestId, cleanerId), (res) => {
    const who = cleaner(cleanerId).name.split(' ')[0];
    const outcome = (res as { outcome?: string } | undefined)?.outcome;
    if (outcome === 'already-applied') toast(who + ' has already applied — look under the request.');
    else if (outcome === 'already-invited') toast(who + ' was already invited.');
    else if (outcome === 'declined') toast(who + ' has turned this job down.');
    else toast('Invited ' + who + ' to this job');
  });

/** Start paying. Works from the first day, not only once the trial has run
 *  out -- somebody who has decided should never be told to come back in a
 *  month. Stripe's own page does the rest. */
/** A code typed in Settings rather than at sign-up. */
export async function redeemPromo(code: string): Promise<boolean> {
  state.busy = true;
  commit();
  try {
    const r = await api.redeemPromo(code);
    absorb(r.state);
    toast(r.message);
    return true;
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t('tx_s_code'));
    return false;
  } finally {
    state.busy = false;
    commit();
  }
}

export async function startSubscription(): Promise<void> {
  state.busy = true;
  commit();
  try {
    const r = await api.checkout();
    if (r.url) { window.location.href = r.url; return; }
    toast('Could not open the payment page.');
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Could not open the payment page.');
  } finally {
    state.busy = false;
    commit();
  }
}

/* Cancelling has to be one tap in the app -- New York requires it to be at
   least as easy as signing up, and it should be anyway. Nothing is taken
   away at the moment of cancelling: they keep what they paid for. */
export const cancelSubscription = () =>
  mutate(() => api.cancelSubscription(), () =>
    toast('Cancelled. You keep everything until the end of the month you paid for.'));

export const resumeSubscription = () =>
  mutate(() => api.resumeSubscription(), () => toast('Your subscription will renew again.'));

export const stopLooking = (requestId: string) =>
  mutate(() => api.closeRequest(requestId), () => {
    back();
    toast('Your home is hidden from cleaners again.');
  });

export function focusFind(requestId: string): void {
  const r = byId(REQUESTS, requestId);
  if (!r) return;
  state.ctx = r.id;
  state.filters.people = blankPeopleFilter();
  state.filters.people.date = r.date;
  state.filters.people.areas = [house(r.houseId).area];
  state.filters.people.tags = r.jobIds.filter((j) => job(j).skill);
  tab('h_find');
}

export function clearCtx(): void {
  state.ctx = null;
  state.filters.people = blankPeopleFilter();
  commit();
}

/* --------------------------------------------------------------- posting */

export const blankDraft = (houseId: string): Draft => ({
  houseId, jobIds: [], date: addDays(TODAY_ISO, 3), start: '09:00',
  hours: 4, rate: 30, payMethods: ['cash', 'zelle'], notes: '', recurring: null,
});

function recurrenceFor(d: Draft): Recurrence | null {
  if (!d.recurring) return null;
  return { freq: 'weekly', day: DOW[parseDate(d.date).getDay()] as Weekday, openEnded: true };
}

const draftBody = (d: Draft) => ({
  houseId: d.houseId, date: d.date, start: d.start, hours: d.hours, rate: d.rate,
  jobIds: d.jobIds, payMethods: d.payMethods, notes: d.notes, recurring: recurrenceFor(d),
});

export const postRequest = () => {
  const d = state.draft!;
  return mutate(() => api.createRequest(draftBody(d)), (r) => {
    state.draft = null;
    state.stack = [];
    state.view = 'h_request';
    state.params = { id: r.id! };
    toast('You are live. Cleaners nearby can see you now.');
  });
};

export function startEditRequest(requestId: string): void {
  const r = byId(REQUESTS, requestId);
  if (!r) return;
  state.draft = {
    id: r.id, houseId: r.houseId, jobIds: r.jobIds.slice(), date: r.date, start: r.start,
    hours: r.hours, rate: r.rate, payMethods: r.payMethods.slice(), notes: r.notes,
    recurring: r.recurring,
  };
  nav('h_editrequest');
}

export const saveEditedRequest = () => {
  const d = state.draft!;
  return mutate(() => api.updateRequest(d.id!, draftBody(d)), () => {
    state.draft = null;
    back();
    toast('Updated. Everyone who applied has been told.');
  });
};

/* -------------------------------------------------------------- job list */

const saveList = (list: unknown[]) => api.saveJobList(state.houseId, list);

export const addJob = (jobId: string) => {
  const list = [...activeHouse().jobList, { jobId, note: '' }];
  return mutate(() => saveList(list));
};

export const removeJob = (i: number) => {
  const list = activeHouse().jobList.filter((_, idx) => idx !== i);
  return mutate(() => saveList(list));
};

/** A job the catalog never had. The id is prefixed so everything downstream
 *  can tell it apart, and the name travels with it on the home's list. */
export const addCustomJob = (label: string, note: string) => {
  const clean = label.trim().slice(0, 60);
  if (!clean) return Promise.resolve(null);
  const jobId = 'custom_' + Math.random().toString(36).slice(2, 9);
  const list = [...activeHouse().jobList, { jobId, label: clean, note: note.trim() }];
  return mutate(() => saveList(list), () => {
    state.sheet = null;
    toast('Added ' + clean);
  });
};

export const saveJobNote = (i: number, note: string) => {
  const list = activeHouse().jobList.map((j, idx) => (idx === i ? { ...j, note } : j));
  return mutate(() => saveList(list), () => {
    state.sheet = null;
    toast('Note saved');
  });
};

/* -------------------------------------------------------------- calendar */

export const saveDays = (
  days: IsoDate[],
  mode: 'available' | 'block' | 'off',
  opts: { from?: Clock; to?: Clock; wFrom?: Clock; wTo?: Clock; reason?: string },
) =>
  mutate(() => api.setDays({ days, mode, ...opts }), () => {
    const n = days.length;
    state.selDays = [];
    state.sheet = null;
    toast(tf(n > 1 ? 'tx_s_days_n' : 'tx_s_days_1', { n }));
  });

export const addTimeOff = (from: IsoDate, to: IsoDate, reason: string) =>
  mutate(() => api.timeOff({ from, to, reason }), () => {
    state.sheet = null;
    toast(t('tx_s_timeoff'));
  });

export const removeTimeOff = (i: number) => mutate(() => api.timeOff({ removeIndex: i }));

export const removeBlock = (iso: IsoDate, i: number) =>
  mutate(() => api.removeBlock(iso, i));

/* The usual week is edited locally, then saved in one go. */
export function toggleWeekday(d: Weekday): void {
  const av = AVAILABILITY[state.cleanerId];
  av.weekly[d] = (av.weekly[d] || []).length ? [] : ['09:00-17:00'];
  commit();
}

export function setWeekdayTime(d: Weekday, which: 'from' | 'to', value: Clock): void {
  const av = AVAILABILITY[state.cleanerId];
  const cur = (av.weekly[d] || ['09:00-17:00'])[0].split('-');
  if (which === 'from') cur[0] = value; else cur[1] = value;
  av.weekly[d] = [cur.join('-')];
  commit();
}

export const saveWeek = () =>
  mutate(() => api.weekly(AVAILABILITY[state.cleanerId].weekly), () => {
    back();
    toast(t('tx_s_week'));
  });

export const toggleHidden = () => {
  const now = AVAILABILITY[state.cleanerId].hidden;
  return mutate(() => api.visibility(!now), () =>
    toast(!now ? t('tx_s_hidden') : t('tx_s_visible')));
};

/* ------------------------------------------------------------------ chat */

export const openChat = (opts: { houseId?: string; cleanerId?: string }) =>
  mutate(
    () => api.openThread({
      houseId: state.role === 'house' ? state.houseId : opts.houseId,
      cleanerId: state.role === 'cleaner' ? state.cleanerId : opts.cleanerId,
    }),
    (r) => nav(state.role === 'house' ? 'h_chat' : 'c_chat', { id: r.id! }),
  );

/* Opening the Updates screen is reading them. The list is sent up as it was
   shown, with each item's own moment, so anything that changes afterwards
   comes back new on its own.

   Deliberately not a mutation: this must not swap the whole world out from
   under the screen the moment it opens, or every update would turn from new
   to read while somebody is still looking at the list. The markers are
   written locally so the badge clears now, and the server agrees next time
   anything is loaded. */
export function markUpdatesRead(): void {
  const items = NOTIFICATIONS[state.role]
    .filter((n) => n.unread)
    .map((n) => ({ id: n.id, at: n.at }));
  if (items.length === 0) return;

  for (const it of items) NOTIF_SEEN[it.id] = it.at;
  for (const n of NOTIFICATIONS[state.role]) n.unread = false;
  commit();

  /* If this does not reach the server they come back as new, which is the
     right way round to fail. */
  void api.markNotificationsSeen(items).catch(() => {});
}

export const sendMessage = (threadId: string, text: string, photoIds?: string[]) => {
  /* A message with nothing but a picture in it is a message. Only the empty
     one is nothing to send. */
  const has = text.trim() || (photoIds && photoIds.length);
  if (!has) return Promise.resolve(null);
  return mutate(() => api.sendMessage(threadId, text.trim(), photoIds));
};

export const sendOffer = (threadId: string, rate: number, hours: number, note: string) =>
  mutate(() => api.sendOffer(threadId, rate, hours, note), () => {
    state.sheet = null;
    toast(t('tx_s_price'));
  });

export const respondToOffer = (threadId: string, i: number, accept: boolean) =>
  mutate(() => api.respondToOffer(threadId, i, accept), () => {
    if (accept) {
      const th = byId(THREADS, threadId);
      const m = th?.messages[i];
      if (m && m.kind === 'offer') toast(tf('tx_s_agreed', { rate: '$' + m.rate }));
    }
  });

export const markThreadRead = (threadId: string) => api.markRead(threadId).catch(() => {});

/* -------------------------------------------------- bookings and reviews */

/** Arriving. This is the moment a booking becomes a visit. */
export const startBooking = (id: string) =>
  mutate(() => api.startBooking(id), () => {
    state.stack = [];
    state.view = 'c_inprogress';
    state.params = { id };
    toast(t('tx_s_started'));
  });

/* ------------------------------------------------- not showing somebody

   Quiet on purpose. The other side is never told, and nothing already agreed
   is undone -- a booking that exists still exists, and still has to happen. */

export const hidePerson = (
  target: { houseId?: string; cleanerId?: string },
  label: string,
) => mutate(() => api.hide({ ...target, hide: true }), () => {
  closeSheet();
  /* Standing on their profile when it vanishes is disorienting. */
  if (state.view === 'h_cleaner' || state.view === 'c_job') back();
  toast(`You will not see ${label} again. Undo in Settings.`);
});

export const unhidePerson = (target: { houseId: string; cleanerId: string }) =>
  mutate(() => api.hide({ ...target, hide: false }), () => {
    loadHidden();
    toast(t('tx_s_back'));
  });

/** Read-only, so it lives outside the bootstrap and is fetched on demand. */
export async function loadHidden(): Promise<void> {
  try {
    const r = await api.hidden();
    state.hidden = r.hidden;
  } catch {
    state.hidden = [];
  }
  commit();
}

export const setJobState = (
  id: string,
  jobId: string,
  patch: { done?: boolean; note?: string },
) => mutate(() => api.setJobState(id, { jobId, ...patch }));

/* "Just this time." Deliberately separate from saveJobList: the whole point
   is that it does not touch what the home has standing. */
export const saveOnce = (id: string, patch: { note?: string; jobIds?: string[] }) =>
  mutate(() => api.onceOnly(id, patch), () => {
    closeSheet();
    toast(t('tx_s_once'));
  });

/* One week of a repeating job. The series is never touched -- that is the
   whole reason this is a separate call and not an edit to the rule. */
export const saveOccurrence = (
  recurringId: string,
  patch: { date: string; jobIds?: string[] | null; note?: string; skipped?: boolean },
) => mutate(() => api.setOccurrence(recurringId, patch), () => {
  closeSheet();
  toast(patch.skipped ? 'That week is off. The rest carry on.' : 'Saved for that week only.');
});

/* Switch which side of Ozeret you are using. Admin only, and the server says
   so again -- half the app is invisible to whoever built it otherwise. */
/** Which layout to draw, whatever the window happens to be. */
export function setViewport(v: 'auto' | 'mobile' | 'desktop'): void {
  state.viewport = v;
  commit();
}

export const viewAs = (role: 'house' | 'cleaner') =>
  mutate(() => api.viewAs(role), () => {
    state.stack = [];
    state.sheet = null;
    state.view = role === 'cleaner' ? 'c_jobs' : 'h_find';
    state.params = {};
    toast(role === 'cleaner' ? 'Looking at Ozeret as a cleaner.' : 'Back to your home.');
  });

export const setPaid = (id: string, paid: boolean) =>
  mutate(() => api.setPaid(id, paid), () => {
    toast(paid ? t('tx_s_paid') : t('tx_s_unpaid'));
  });

export const cancelBooking = (id: string) =>
  mutate(() => api.cancelBooking(id), () => toast(t('tx_s_cancelled')));

export const completeBooking = (id: string) =>
  mutate(() => api.complete(id), () => {
    if (state.view === 'c_inprogress') {
      state.stack = [];
      state.view = 'c_booking';
      state.params = { id };
    }
    toast(t('tx_s_finished'));
  });

export function rebook(id: string): void {
  const b = byId(BOOKINGS, id);
  if (!b) return;
  state.houseId = b.houseId;
  state.draft = {
    houseId: b.houseId, jobIds: b.jobIds.slice(), date: addDays(TODAY_ISO, 3),
    start: b.start, hours: b.hours, rate: b.rate, payMethods: [b.payMethod],
    notes: '', recurring: null,
  };
  tab('h_post');
  toast('Same job list and time, ready to post again.');
}

export const submitReview = (bookingId: string) => {
  const rv = { ...state.reviewDraft } as Review;
  return mutate(() => api.review(bookingId, rv), () => {
    state.reviewDraft = {};
    back();
    toast(t('tx_s_review'));
  });
};

export const logPastJob = () => {
  const e = state.pastJob!;
  return mutate(
    () => api.logPast({ ...e, houseId: state.houseId }),
    (r) => {
      state.pastJob = null;
      state.stack = [];
      state.view = 'review';
      state.params = { id: r.id! };
      toast('Logged. Now tell people what they were like.');
    },
  );
};

/* ------------------------------------------------------- profile editing */

export function startEditCleaner(): void {
  const c = activeCleaner();
  if (!c) return;
  state.edit = {
    name: c.name, headline: c.headline, bio: c.bio, gender: c.gender, rate: c.rate,
    minHours: c.minHours, yearsExp: c.yearsExp, areas: c.areas.slice(),
    baseZip: c.baseZip ?? '', radiusMiles: c.radiusMiles ?? 20,
    langIds: c.languages.map((l) => l.id), traits: c.traits.slice(),
    specialties: c.specialties.slice(),
  };
  nav('c_editprofile');
}

export const saveCleaner = () => {
  const c = activeCleaner();
  if (!c) return Promise.resolve(null);
  const e = state.edit!;
  const languages = e.langIds.map(
    (id: string) => c.languages.find((x) => x.id === id) ?? { id, level: 'good' },
  );
  return mutate(
    () => api.saveCleaner({
      name: e.name, headline: e.headline, bio: e.bio, gender: e.gender, rate: e.rate,
      minHours: e.minHours, yearsExp: e.yearsExp, areas: e.areas,
      baseZip: e.baseZip, radiusMiles: e.radiusMiles,
      traits: e.traits, specialties: e.specialties, languages,
    }),
    () => { state.edit = null; back(); toast(t('tx_s_profile')); },
  );
};

export function startEditHome(): void {
  const h = activeHouse();
  state.edit = {
    displayName: h.displayName, area: h.area, homeType: h.homeType, about: h.about,
    bedrooms: h.bedrooms, bathrooms: h.bathrooms, floors: h.floors, kids: h.kids,
    sqft: h.sqft, stairs: h.stairs, elevator: h.elevator,
    suppliesProvided: h.suppliesProvided, kosherHome: h.kosherHome,
    pets: h.pets, parking: h.parking, address: h.address ?? '', zip: h.zip ?? '',
    langIds: h.languages.map((l) => l.id),
  };
  nav('h_edithome');
}

export const saveHome = () => {
  const h = activeHouse();
  const e = state.edit!;
  const languages = e.langIds.map(
    (id: string) => h.languages.find((x) => x.id === id) ?? { id, level: 'good' },
  );
  return mutate(
    () => api.saveHouse(h.id, { ...e, languages }),
    () => { state.edit = null; back(); toast('Home saved.'); },
  );
};

export function startPastJob(): void {
  state.pastJob = {
    cleanerId: '', date: addDays(TODAY_ISO, -7), start: '09:00',
    hours: 4, rate: 30, jobIds: [],
  };
  nav('h_pastjob');
}

/* ----------------------------------------------------- settings, feedback */

export const FEEDBACK: { topic: string; text: string }[] = [];

export async function sendFeedback(): Promise<void> {
  const entry = { topic: state.fb.topic, text: state.fb.text };
  try {
    await api.feedback({ ...entry, screen: state.view });
    FEEDBACK.push(entry);
    state.fb = { topic: '', text: '' };
    toast(t('tx_s_thanks'));
  } catch {
    toast(t('tx_s_notsent'));
  }
}

export function reportCalendar(
  cleanerId: string, date: string, reasons: string[], detail: string,
): void {
  api.reportCalendar({ cleanerId, date, reasons, detail })
    .then(() => {
      state.sheet = null;
      toast(t('tx_s_report'));
    })
    .catch((e) => toast(e instanceof ApiError ? e.message : t('tx_s_reportfail')));
}

/** The days this home actually invited a cleaner for — the only days it can
 *  say anything about. Recent ones first, since that is what people mean. */
export function invitedDates(cleanerId: string): string[] {
  const cutoff = addDays(TODAY_ISO, -14);
  return REQUESTS
    .filter((r) => HOUSE_IDS.includes(r.houseId) && r.date >= cutoff)
    .filter((r) => r.applicants.some((a) => a.cleanerId === cleanerId))
    .map((r) => r.date)
    .filter((d, i, all) => all.indexOf(d) === i)
    .sort((a, b) => b.localeCompare(a));
}

/* Notification preferences are per-device for now. */
export function setNotifyChannel(id: string): void {
  (NOTIFY_PREFS as any).channel = id;
  commit();
}
export function flipNotify(key: string): void {
  (NOTIFY_PREFS as any)[key] = !(NOTIFY_PREFS as any)[key];
  commit();
}
export function setQuiet(key: 'quietFrom' | 'quietTo', value: Clock): void {
  NOTIFY_PREFS[key] = value;
  commit();
}

export { NOTIFY_PREFS };
